import { useDispatch, useSelector } from 'react-redux';
import { buttonBaseClasses, Icon, IconButton, Pagination as MuiPagination, paginationItemClasses, tablePaginationClasses, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MuiTablePagination from '@mui/material/TablePagination';
import { Box, styled } from '@mui/system';

import isEmpty from 'Lib/utilities/isEmpty';
import { screenSize } from 'Lib/utilities/screenSize';

const TablePagination = styled(MuiTablePagination)(({ theme }) => ({
	[`& .${tablePaginationClasses.select}`]: {
		display: 'flex',
		justifyContent: 'space-around',
		marginRight: theme.spacing(1)
	},
	[`& .${tablePaginationClasses.selectIcon}`]: { fontSize: '20px', marginRight: theme.spacing(4) },
	[`& .${tablePaginationClasses.selectLabel}`]: { marginBottom: 0, fontSize: theme.typography.pxToRem(16) },
	[`& .${tablePaginationClasses.displayedRows}`]: { marginBottom: 0, fontSize: theme.typography.pxToRem(16) }
}));

const StyledPagination = styled(MuiPagination)(({ theme }) => ({
	alignSelf: 'center',
	[`& .${paginationItemClasses.page}`]: { borderRadius: '10px', fontSize: theme.typography.pxToRem(16) },
	[`& .${buttonBaseClasses.root}`]: {
		'&:hover': { fontWeight: theme.typography.fontWeightSemiBold, backgroundColor: 'unset' },
		[`& .${paginationItemClasses.icon}`]: { fontSize: '2rem' },
		'&.Mui-selected': {
			backgroundColor: theme.palette.common.lightblue,
			'&:hover': { backgroundColor: theme.palette.common.lightblue }
		}
	}
}));

export function TablePaginationActions(props) {
	const theme = useTheme();
	const { page, onPageChange, totalPages, pageChange } = props;

	const handleBackButtonClick = event => {
		onPageChange(event, page - 1);
	};
	const handleNextButtonClick = event => {
		onPageChange(event, page + 1);
	};

	return (
		<Box sx={{ flexShrink: 0, ml: 5, display: 'flex', alignItems: 'center' }}>
			<Typography>Pages</Typography>
			<IconButton onClick={handleBackButtonClick} disabled={page < 1 || totalPages === 1} aria-label="previous page">
				{theme.direction === 'rtl' ? <Icon>arrow_forward_ios</Icon> : <Icon>arrow_back_ios_new</Icon>}
			</IconButton>
			<StyledPagination hideNextButton={true} hidePrevButton={true} page={page + 1} count={totalPages} onChange={pageChange} />
			<IconButton onClick={handleNextButtonClick} disabled={page >= totalPages - 1 || totalPages === 1} aria-label="next page">
				{theme.direction === 'rtl' ? <Icon>arrow_back_ios_new</Icon> : <Icon>arrow_forward_ios</Icon>}
			</IconButton>
		</Box>
	);
}

export default function Pagination({ pagination, onPageChange, actions }) {
	const dispatch = useDispatch();

	const { page, totalItems, totalPages } = pagination;

	const { mobile } = screenSize();

	const pageSize = useSelector(state => state.ui.table.pageSize) || pagination.pageSize;

	const handlePageChange = event => {
		if (actions) {
			dispatch(actions.setTablePageSize(event.target.value));
		}

		onPageChange(null, event.target.value);
	};

	return isEmpty(pagination) || pageSize == 9999 ? (
		<Box sx={{ py: 6 }} />
	) : (
		<Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: { xs: 0, sm: mobile ? 0 : 4 } }}>
			{/* This pagination represents rows per page, it's select, and information related to what is showing (e.g. "1-25 of 600") */}
			<TablePagination
				component="div"
				sx={{ py: '4px !important' }}
				page={Number(page) - 1}
				count={Number(totalItems)}
				rowsPerPage={Number(pageSize)}
				rowsPerPageOptions={[15, 25, 50, 100]}
				onPageChange={(__, page) => onPageChange(page + 1)}
				onRowsPerPageChange={handlePageChange}
				ActionsComponent={actionsProps => <TablePaginationActions {...actionsProps} totalPages={Number(totalPages)} pageChange={(__, page) => onPageChange(page)} />} // This prop represents pages and the previous/next buttons
				slotProps={{
					select: {
						name: 'Rows per Page',
						IconComponent: props => <Icon {...props}>expand_more</Icon>,
						sx: {
							mr: 2,
							my: 3,
							height: 40,
							width: 99,
							borderRadius: 10,
							border: theme => `1px solid ${theme.palette.common.black}`,
							fontSize: theme => theme.typography.pxToRem(16)
						}
					}
				}}
			/>
		</Box>
	);
}
